import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { useState } from 'react';

import { Modal } from '@toolbox/components';
import { CloseIcon } from '@toolbox/components/Icon/icons/CloseIcon';
import { QuestionMarkIcon } from '@toolbox/components/Icon/icons/QuestionMarkIcon';

export type AssistedGuidanceModalProps = {
  modalCta?: string;
  modalTitle?: string;
  gridSize?: number;
  guidances?: Guidance[];
};

export type Guidance = {
  title?: string | null;
  description?: string | null;
  helperText?: string | null;
  image?:
    | {
        filename?: string | null;
        id?: number | null;
        alt?: string | null;
      }
    | null
    | '';
};

const classes = {
  modalCtaContainer: (theme: Theme) => css`
    display: flex;
    cursor: pointer;
    margin-top: ${theme.spacing(4)};
    justify-content: flex-start;
    align-items: center;
    column-gap: ${theme.spacing(2)};
    outline: none !important;
    span {
      color: #2f3034;
      font-size: 14px;
    }
  `,
  titleContainer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid #e6e6e6;
    span {
      font-size: 18px;
      font-weight: 600;
    }
  `,
  guidanceContainer: css`
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  `,
  helperContainer: (theme: Theme) => css`
    display: flex;
    column-gap: 16px;
    row-gap: 16px;
    ${theme.media.down('sm')} {
      flex-direction: column;
    }
  `,
  image: (theme: Theme) => css`
    flex: 1;
    width: 200px;
    ${theme.media.down('sm')} {
      display: none;
    }
  `,
  imageMobile: (theme: Theme) => css`
    display: none;
    width: 100%;
    max-height: 200px;
    ${theme.media.down('sm')} {
      display: inline-block;
    }
  `,
};

const BodyContainer = styled.div<{ gridSize?: number }>`
  padding: 32px;
  display: grid;
  grid-template-columns: ${({ gridSize }) =>
    gridSize == 2 ? 'calc(50% - 6px) calc(50% - 6px)' : '100%'};

  ${({ theme }) => theme.media.down('sm')} {
    grid-template-columns: 100%;
  }
  flex-direction: column;
  row-gap: 40px;
  column-gap: 40px;
  max-height: 80vh;
  overflow: hidden;
  overflow-y: scroll;
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */

  ::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }
`;

const ModalContainer = styled.div<{ gridSize?: number }>`
  max-width: ${({ gridSize }) => (gridSize == 2 ? '1100px' : '556px')};
  width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  background-color: #ffffff;
  border-radius: 2px;
`;

export const AssistedGuidanceModal = ({
  modalCta,
  modalTitle,
  guidances,
  gridSize,
}: AssistedGuidanceModalProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        css={classes.modalCtaContainer as any}
        onKeyPress={() => setIsOpen((x) => !x)}
        onClick={() => setIsOpen((x) => !x)}
        tabIndex={0}
        role={'button'}
      >
        <span>{modalCta}</span>
        <QuestionMarkIcon />
      </div>
      <Modal
        onRequestClose={() => {
          setIsOpen(false);
        }}
        isOpen={isOpen}
      >
        <ModalContainer gridSize={gridSize}>
          <div css={classes.titleContainer as any}>
            <span>{modalTitle}</span>
            <CloseIcon
              css={{ width: 13, cursor: 'pointer' } as any}
              onClick={() => setIsOpen(false)}
              tabIndex={0}
              role={'button'}
            />
          </div>
          <BodyContainer gridSize={gridSize}>
            {guidances?.map((guidance) => (
              <div css={classes.guidanceContainer as any} key={guidance.title}>
                <div css={{ fontWeight: 'bold', fontSize: '16px', lineHeight: '28px' } as any}>
                  {guidance.title}
                </div>
                {guidance.image && guidance.image.filename && (
                  <div css={{ display: 'flex', justifyContent: 'center' } as any}>
                    <img
                      css={classes.imageMobile as any}
                      src={guidance.image.filename}
                      alt={guidance.image.alt || ''}
                    />
                  </div>
                )}
                <div css={{ fontWeight: 'normal', fontSize: '16px', lineHeight: '28px' } as any}>
                  {guidance.description}
                </div>
                <div css={classes.helperContainer as any}>
                  {guidance.image && guidance.image.filename && (
                    <img
                      css={classes.image as any}
                      src={guidance.image.filename}
                      alt={guidance.image.alt || ''}
                    />
                  )}
                  <div css={{ fontWeight: 'normal', flex: 1, lineHeight: '28px' } as any}>
                    {guidance.helperText}
                  </div>
                </div>
              </div>
            ))}
          </BodyContainer>
        </ModalContainer>
      </Modal>
    </>
  );
};
